import { useMemo } from 'react';

import { AmaEvent, UserStatusApiResponse } from '../types/api.types';

import { toUnixTimestamp } from '../helpers/date-format.helper';

type UseAmaCodeVerificationStateParams = {
  amaEvent: AmaEvent | null;
  userStatus: UserStatusApiResponse | undefined;
  startDatetime: string;
};

type UseAmaCodeVerificationStateReturn = {
  codeValidFrom: number | null;
  codeValidTo: number | null;
  isCodeWindowActive: boolean;
  isCountdownActive: boolean;
  shouldShowCodeInput: boolean;
  shouldShowCountdown: boolean;
  countdownTarget: number | null;
  isEventStarted: boolean;
};

export const useAmaCodeVerificationState = ({
  amaEvent,
  userStatus,
  startDatetime,
}: UseAmaCodeVerificationStateParams): UseAmaCodeVerificationStateReturn => {
  const isCodeVerified = userStatus?.ama_verification?.status === 'VERIFIED';
  const isUserRegistered = userStatus?.status === 'REGISTERED';
  const requiresCodeVerification = amaEvent?.requires_code_verification ?? false;

  const eventStartTimestamp = useMemo(() => toUnixTimestamp(startDatetime), [startDatetime]);

  const codeValidFrom = useMemo(
    () => (amaEvent?.code_valid_from ? toUnixTimestamp(amaEvent.code_valid_from) : null),
    [amaEvent]
  );

  const codeValidTo = useMemo(
    () => (amaEvent?.code_valid_to ? toUnixTimestamp(amaEvent.code_valid_to) : null),
    [amaEvent]
  );

  const isEventStarted = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);
    return now >= eventStartTimestamp;
  }, [eventStartTimestamp]);

  const isCodeWindowActive = useMemo(() => {
    if (!codeValidFrom || !codeValidTo) return true;
    const now = Math.floor(Date.now() / 1000);
    return now >= codeValidFrom && now <= codeValidTo;
  }, [codeValidFrom, codeValidTo]);

  const isCountdownActive = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);

    // If event hasn't started yet, countdown is active
    if (now < eventStartTimestamp) return true;

    // If event started but code window hasn't opened yet
    if (codeValidFrom && now < codeValidFrom) return true;

    return false;
  }, [eventStartTimestamp, codeValidFrom]);

  const countdownTarget = useMemo(() => {
    const now = Math.floor(Date.now() / 1000);

    // If event hasn't started yet, count down to event start
    if (now < eventStartTimestamp) return eventStartTimestamp;

    // If event started but code window hasn't opened, count down to code window
    if (codeValidFrom && now < codeValidFrom) return codeValidFrom;

    return null;
  }, [eventStartTimestamp, codeValidFrom]);

  const shouldShowCodeInput = useMemo(() => {
    return isUserRegistered && requiresCodeVerification && !isCodeVerified && isCodeWindowActive && isEventStarted;
  }, [isUserRegistered, requiresCodeVerification, isCodeVerified, isCodeWindowActive, isEventStarted]);

  const shouldShowCountdown = useMemo(() => {
    // Show countdown if verification is required and countdown is active
    // Don't show if user is registered and already verified
    if (!requiresCodeVerification) return false;
    if (isCodeVerified) return false;
    if (!isCountdownActive) return false;

    // Show countdown for non-registered users or registered users who haven't verified
    return !isUserRegistered || (isUserRegistered && !isCodeVerified);
  }, [isUserRegistered, requiresCodeVerification, isCodeVerified, isCountdownActive]);

  return {
    codeValidFrom,
    codeValidTo,
    isCodeWindowActive,
    isCountdownActive,
    shouldShowCodeInput,
    shouldShowCountdown,
    countdownTarget,
    isEventStarted,
  };
};
